/**
 * SharedReport — public, read-only view of a caregiver-approved
 * snapshot. Accessed at `/r/:token`. No auth required — the link
 * itself is the credential. Expires 7 days after creation.
 *
 * The report renders exactly what the caregiver approved, with the
 * same disclaimer footer and the same coral highlight for the "major"
 * sections. Nothing else on Memory Lane is exposed.
 */

import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, HeartHandshake, ArrowLeft, Compass } from "lucide-react";

export default function SharedReport() {
    const { token } = useParams();
    const [doc, setDoc] = useState(null);
    const [status, setStatus] = useState("loading"); // loading | ok | expired | notfound

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const r = await api.get(`/care-team/snapshot/${encodeURIComponent(token)}`);
                if (!active) return;
                setDoc(r.data);
                setStatus("ok");
            } catch (err) {
                if (!active) return;
                if (err?.response?.status === 410) setStatus("expired");
                else setStatus("notfound");
            }
        })();
        return () => { active = false; };
    }, [token]);

    if (status === "loading") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FFF9F1] text-[#4C5551]">
                <Loader2 className="animate-spin mr-2" size={16} /> Loading the update…
            </div>
        );
    }

    if (status !== "ok") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FFF9F1] px-6" data-testid="shared-report-error">
                <div className="max-w-md rounded-3xl border border-[#E2E1D8] bg-white p-6 md:p-8 text-center">
                    <HeartHandshake size={28} className="mx-auto text-[#8B5A2B]" />
                    <h1 className="mt-3 font-heading text-2xl text-[#12776F]">
                        {status === "expired" ? "This link has expired." : "This link is no longer available."}
                    </h1>
                    <p className="mt-2 text-[15px] text-[#4C5551]">
                        Shared Memory Lane updates expire seven days after they&apos;re created.
                        Ask the caregiver to send you a fresh link.
                    </p>
                    <Link to="/" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-dark hover:underline">
                        <ArrowLeft size={14} /> Learn about Memory Lane
                    </Link>
                </div>
            </div>
        );
    }

    const meta = doc.meta || {};
    const sections = doc.sections || [];

    return (
        <div className="min-h-screen bg-[#FFF9F1] py-8 px-4 md:px-6" data-testid="shared-report">
            <div className="max-w-3xl mx-auto">
                <div className="mb-4 flex items-center justify-between gap-3">
                    <Link to="/" className="inline-flex items-center gap-2 text-[#8B5A2B] hover:text-[#5C3B18]">
                        <span className="w-8 h-8 rounded-2xl bg-teal-dark text-white flex items-center justify-center">
                            <Compass strokeWidth={1.8} size={15} />
                        </span>
                        <span className="text-sm font-semibold">Memory Lane</span>
                    </Link>
                    <button
                        type="button"
                        onClick={() => window.print()}
                        data-testid="shared-print"
                        className="text-xs font-semibold text-teal-dark hover:underline"
                    >
                        Print or save as PDF
                    </button>
                </div>

                <article
                    className="rounded-3xl border border-[#E2E1D8] bg-white p-6 md:p-9 leading-relaxed"
                    style={{ color: "#1F2624" }}
                    data-testid="shared-report-body"
                >
                    <header className="border-b border-[#E2E1D8] pb-4">
                        <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8B5A2B]">Memory Lane</p>
                        <h1 className="mt-1 font-heading font-semibold text-2xl md:text-3xl">Caregiver Update</h1>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-6 text-sm text-[#4C5551]">
                            {meta.person_receiving_care && <p><strong>Person receiving care:</strong> {meta.person_receiving_care}</p>}
                            {meta.caregiver_name && <p><strong>Caregiver:</strong> {meta.caregiver_name}{meta.caregiver_relationship && <> ({meta.caregiver_relationship})</>}</p>}
                            {meta.date_prepared && <p><strong>Date prepared:</strong> {formatDate(meta.date_prepared)}</p>}
                            {meta.date_range && <p><strong>Date range:</strong> {humanRange(meta.date_range, meta.custom_start, meta.custom_end)}</p>}
                            {doc.recipient && <p><strong>Intended recipient:</strong> {doc.recipient}</p>}
                            {meta.purpose && <p><strong>Reason for update:</strong> {meta.purpose_label || meta.purpose}</p>}
                        </div>
                    </header>

                    <div className="mt-5 space-y-5">
                        {sections.map((s, i) => {
                            const isMajor = s.key === "major_changes" || s.key === "safety_concerns";
                            if (!s.body || !s.body.trim()) return null;
                            return (
                                <section key={i} data-testid={`shared-section-${s.key}`}>
                                    <h2 className={`text-sm font-heading font-semibold ${isMajor ? "text-[#7A2E13]" : "text-[#1F2624]"}`}>{s.label || s.key}</h2>
                                    <div className={`mt-1 text-[15px] whitespace-pre-wrap ${isMajor ? "text-[#5C2712]" : "text-[#1F2624]"}`}>{s.body}</div>
                                </section>
                            );
                        })}

                        {doc.caregiver_note?.trim() && (
                            <section>
                                <h2 className="text-sm font-heading font-semibold">Additional caregiver note</h2>
                                <p className="mt-1 text-[15px] whitespace-pre-wrap">{doc.caregiver_note}</p>
                            </section>
                        )}
                    </div>

                    <footer className="mt-8 border-t border-[#E2E1D8] pt-4 text-xs text-[#4C5551] italic">
                        This report contains caregiver-entered information intended to support communication.
                        It is not a clinical assessment, diagnosis, or medical advice.
                        {doc.expires_at && <> · Link expires {formatDate(doc.expires_at)}.</>}
                    </footer>
                </article>

                <p className="text-center text-xs text-[#8B5A2B] mt-4">
                    Shared through <Link to="/" className="underline">Memory Lane</Link> — free caregiver navigation platform.
                </p>
            </div>
        </div>
    );
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch { return String(iso).slice(0, 10); }
}

function humanRange(key, start, end) {
    if (key === "custom") return `${formatDate(start)} — ${formatDate(end)}`;
    const map = { since_last: "Since my last update", "7d": "Last 7 days", "30d": "Last 30 days" };
    return map[key] || key;
}
